import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Input, Select, SelectItem, Card } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';

import { 
  PageHeader, 
  CtyButton, 
  CtySpinner, 
  ErrorBoundary, 
  checkType, 
  CtyShowToast,
  useAgGridStatePersistence,
  SimpleDeleteConfirmationModal,
  useHasApplicationResource,
  AuditLogsAgGridWrapper,
  CustomTabs,
  SCHEDULER_AUDIT_LOG_STATIC_FILTERS,
} from '@circuitry-ai/doc-ui';
import {
  useGetSchedulesQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
  useDeleteScheduleMutation,
  useGetScheduleDetailsQuery,
  useRunScheduleMutation,
} from '../../../shared/data/src/features/settings/settingsApiSlice';
import type { Schedule, ScheduleType } from 'modules/shared/data/src/features/settings/settingstypes';
import { getSchedules,
         STATE_OPTIONS, 
         getScheduleTypeClass,  
         scheduleTypeOptions, 
         getScheduleStateClass, 
         craftSchedulePayload,
         addSchedulePatternToPayload,
         getFilteredSchedules,
         getBuildSchedulePayload,
         processCustomParamsFromSchedule,
         determineScheduleType,
         populateScheduleTypeFields,
         applyCustomParamsToPayload} from './scheduler-utils';
import { ScheduleDetailsViewModal } from './components/ScheduleDetailsViewModal';
import { ActivityLogModal } from './components/ActivityLogModal';
import useScheduleSharedComponents from './schedule-shared-components';
import ReprocessSettings from './reprocess-settings';

const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);

const handleSelectChange = (onChange: (value: string) => void) => (keys: unknown) => {
  const value = Array.from(keys as Set<string>)[0];
  onChange(value || '');
};

// Actions cell renderer for scheduler AG Grid
const ScheduleActionsCell = React.memo((props: {
  data?: Schedule;
  onView?: (id: string) => void;
  onEdit?: (schedule: Schedule) => void;
  onDelete?: (id: string, name: string) => void;
  isDisabled?: boolean;
}) => {
  const { data, onView, onEdit, onDelete } = props;

  const actions = useMemo(() => {
    if (!data) return [];
    return [
      { key: 'view', icon: 'mdi:eye', labelKey: 'View', onPress: () => onView?.(data.scheduler_id) },
      { key: 'edit', icon: 'mdi:pencil', labelKey: 'Edit', onPress: () => onEdit?.(data) },
      { key: 'delete', icon: 'mdi:delete', labelKey: 'Delete', onPress: () => onDelete?.(data.scheduler_id, data.schedule_name), color: 'danger' as const },
    ];
  }, [data, onView, onEdit, onDelete]);

  if (!data) return null;

  return (
    <div className="flex gap-2 items-center h-full">
      {actions.map(({ key, icon, labelKey, onPress, color }) => (
        <CtyButton
          key={key}
          size="sm"
          variant="light"
          {...(color && { color })}
          isIconOnly
          onPress={onPress}
          aria-label={labelKey}
          isDisabled={props.isDisabled}
        >
          <Icon icon={icon} />
        </CtyButton>
      ))}
    </div>
  );
});
ScheduleActionsCell.displayName = 'ScheduleActionsCell';

export interface CustomParam {
  key: string;
  value: string;
}

export type CustomParamMode = 'key_value' | 'object';

export interface ScheduleFormData {
  schedule_name: string;
  target_id: string;
  target_type: string;
  schedule_type: ScheduleType;
  rateValue?: number;
  rateUnit?: string;
  rateStartDate?: string;
  rateStartTime?: string;
  rateEndCondition?: 'never' | 'specific_date';
  rateEndDate?: string;
  scheduleAt?: string;
  oneTimeDate?: string;
  oneTimeTime?: string;
  // Recurring fields
  recurringFrequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  recurringRepeatEvery?: number;
  recurringDays?: string[];
  recurringStartDate?: string;
  recurringStartTime?: string;
  recurringDayOfMonth?: number | 'l';
  recurringEndCondition?: 'never' | 'specific_date';
  recurringEndDate?: string;
  description: string;
  state: 'ENABLED' | 'DISABLED';
  timezone: string;
  credential_id?: string;
  custom_params?: CustomParam[];
}

// Shared default values to reduce boilerplate
const defaultFormValues: ScheduleFormData = {
  schedule_name: '',
  target_id: '',
  target_type: '',
  schedule_type: scheduleTypeOptions.RATE,
  rateValue: 5,
  rateUnit: 'minutes',
  rateStartDate: '',
  rateStartTime: '',
  rateEndCondition: 'never',
  rateEndDate: '',
  scheduleAt: '',
  oneTimeDate: '',
  oneTimeTime: '',
  recurringFrequency: 'DAILY',
  recurringRepeatEvery: 1,
  recurringDays: [],
  recurringStartDate: '',
  recurringStartTime: '',
  recurringDayOfMonth: 1,
  recurringEndCondition: 'never',
  recurringEndDate: '',
  description: '',
  state: 'DISABLED',
  timezone: 'UTC',
  custom_params: [{ key: '', value: '' }],
};


export function SchedulerSettings() {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState('scheduler');
  const hasSchedulerAccess = useHasApplicationResource('scheduler');
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetScheduleName, setDeleteTargetScheduleName] = useState('');
  const [activityLogSchedulerId, setActivityLogSchedulerId] = useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'add' | 'edit' | null>(null);

  const [customParamMode, setCustomParamMode] = useState<CustomParamMode>('key_value');
  const [customParamJson, setCustomParamJson] = useState('');
  const [customParamJsonError, setCustomParamJsonError] = useState('');

  const { data: schedulesData, isFetching : isLoading, refetch } = useGetSchedulesQuery();
  const { DescriptionField, TargetConfigurationsSection, ScheduleConfigurationSection } = useScheduleSharedComponents();
  
  // Ensure schedules is always an array
  const schedules = getSchedules(schedulesData);
  const [createSchedule, { isLoading: isCreating }] = useCreateScheduleMutation();
  const [updateSchedule, { isLoading: isUpdating }] = useUpdateScheduleMutation();
  const [deleteSchedule, { isLoading: isDeleting }] = useDeleteScheduleMutation();
  const [runSchedule, { isLoading: isRunningSchedule }] = useRunScheduleMutation();
  const { data: scheduleDetails, isLoading: isLoadingDetails, isFetching: isFetchingDetails } = useGetScheduleDetailsQuery(selectedScheduleId || '', { skip: !selectedScheduleId });

  // Separate form instances to prevent interference between create and edit
  const createForm = useForm<ScheduleFormData>({ defaultValues: defaultFormValues });
  const editForm = useForm<ScheduleFormData>({ defaultValues: defaultFormValues });

  // Field arrays for custom parameters
  const createCustomParamsFields = useFieldArray({
    control: createForm.control,
    name: 'custom_params',
  });

  const editCustomParamsFields = useFieldArray({
    control: editForm.control,
    name: 'custom_params',
  });

  // Create form helpers
  const watchedScheduleType = createForm.watch('schedule_type');
  
  // Edit form helpers  
  const watchedEditScheduleType = editForm.watch('schedule_type');

  const filteredSchedules = getFilteredSchedules(schedules, searchTerm, stateFilter, typeFilter);

  const { augmentGridReady, mergeColumnDefs } = useAgGridStatePersistence('scheduler-settings-table');
  const onSchedulerGridReady = useCallback((params: { api: any }) => {
    augmentGridReady(params, (p) => {
      p?.api?.sizeColumnsToFit?.();
    });
  }, [augmentGridReady]);

  const handleCustomParamJsonChange = (value: string) => {
    setCustomParamJson(value);
    if (!value.trim()) {
      setCustomParamJsonError('');
      return;
    }
    try {
      const parsed = JSON.parse(value);
      const isValidObj = typeof parsed === 'object' && !Array.isArray(parsed) && parsed !== null;
      setCustomParamJsonError(isValidObj ? '' : t('Invalid JSON object'));
    } catch {
      setCustomParamJsonError(t('Invalid JSON object'));
    }
  };

  const resetCustomParamState = () => {
    setCustomParamMode('key_value');
    setCustomParamJson('');
    setCustomParamJsonError('');
  };

  const onSubmit = async (data: ScheduleFormData) => {
    try {
      const payload = getBuildSchedulePayload(data);
      
      const result = applyCustomParamsToPayload(payload, data, customParamMode, customParamJson);
      if (result.error) {
        setCustomParamJsonError(t('Invalid JSON object'));
        return;
      }
      
      addSchedulePatternToPayload(payload, data);
      
      await createSchedule(payload).unwrap();
      CtyShowToast(checkType.success, t('Schedule created successfully'));
      createForm.reset(defaultFormValues);
      resetCustomParamState();
      setPanelMode(null);
      refetch();
    } catch (error) {
      CtyShowToast(checkType.error, `${t('Failed to create schedule')}: ${(error as Error)?.message || 'Something went wrong!'}`);
    }
  };

  const handleView = useCallback((scheduleId: string) => {
    setSelectedScheduleId(scheduleId);
    setViewModalOpen(true);
  }, []);

  const handleEdit = useCallback((schedule: Schedule) => {
    setSelectedScheduleId(schedule.scheduler_id);
    const scheduleAny = schedule as any;
    const expressionMetadata = scheduleAny.expression_metadata;

    const keyType = schedule.meta_data?.custom_params_key_type;
    if (keyType === 'object') {
      setCustomParamMode('object');
      const cp = scheduleAny.custom_params;
      setCustomParamJson(cp && typeof cp === 'object' && Object.keys(cp).length > 0 ? JSON.stringify(cp, null, 2) : '');
      setCustomParamJsonError('');
    } else {
      resetCustomParamState();
    }
    
    // Build base edit form values object
    const editValues: ScheduleFormData = {
      ...defaultFormValues,
      schedule_name: schedule.schedule_name,
      target_id: schedule.target_id,
      target_type: schedule.target_type || '',
      schedule_type: determineScheduleType(scheduleAny.simplified_type) as any,
      description: schedule.description || '',
      state: schedule.state,
      timezone: schedule.schedule_timezone || schedule.timezone || 'UTC',
      credential_id: scheduleAny.credential_id || undefined,
      custom_params: processCustomParamsFromSchedule(scheduleAny),
    };

    // Populate schedule type-specific fields
    populateScheduleTypeFields(editValues, expressionMetadata, scheduleAny);

    // Reset edit form with schedule values (doesn't affect create form)
    editForm.reset(editValues);
    setPanelMode('edit');
  }, [editForm]);

  const handleRunNow = async () => {
    if (!selectedScheduleId) return;

    try {
      await runSchedule(selectedScheduleId).unwrap();
      CtyShowToast(checkType.success, t('Schedule run started successfully'));
    } catch (error) {
      CtyShowToast(checkType.error, `${t('Failed to run schedule')}: ${(error as Error)?.message || 'Something went wrong!'}`);
    }
  };

  const handleUpdate = async (data: ScheduleFormData) => {
    if (!selectedScheduleId) return;
    
    const payload = craftSchedulePayload(data);
    if (!payload) return;
    
    const result = applyCustomParamsToPayload(payload, data, customParamMode, customParamJson);
    if (result.error) {
      setCustomParamJsonError(t('Invalid JSON object'));
      return;
    }
    
    addSchedulePatternToPayload(payload, data, { isUpdate: true });
    
    try {
      await updateSchedule({ schedulerId: selectedScheduleId, ...payload }).unwrap();
      CtyShowToast(checkType.success, t('Schedule updated successfully'));
      setPanelMode(null);
      setSelectedScheduleId(null);
      editForm.reset(defaultFormValues);
      resetCustomParamState();
      refetch();
    } catch (error) {
      CtyShowToast(checkType.error, `${t('Failed to update schedule')}: ${(error as Error)?.message || 'Something went wrong!'}`);
    }
  };

  const handleDelete = useCallback((scheduleId: string, scheduleName: string) => {
    setSelectedScheduleId(scheduleId);
    setDeleteTargetScheduleName(scheduleName);
    setDeleteModalOpen(true);
  }, []);

  const confirmDelete = async () => {
    if (!selectedScheduleId) return;

    try {
      await deleteSchedule(selectedScheduleId).unwrap();
      CtyShowToast(checkType.success, t('Schedule deleted successfully'));
      setDeleteModalOpen(false);
      setSelectedScheduleId(null);
      setDeleteTargetScheduleName('');
      refetch();
    } catch (error) {
      CtyShowToast(checkType.error, `${t('Failed to delete schedule')}: ${(error as Error)?.message || 'Something went wrong!'}`);
    }
  };

  const schedulerColumnDefs = useMemo<ColDef<Schedule>[]>(() => [
    {
      headerName: t('Schedule Name'),
      field: 'schedule_name',
      flex: 1,
      minWidth: 160,
      cellRenderer: (params: { data?: Schedule }) => params.data ? (
          <p className="font-semibold truncate" title={params.data.schedule_name}>{params.data.schedule_name}</p>
      ) : null,
    },
    {
      headerName: t('Type'),
      field: 'schedule_type',
      minWidth: 110,
      cellRenderer: (params: { data?: Schedule }) => {
        if (!params.data) return null;
        const simplifiedType = (params.data as any).simplified_type || params.data.schedule_type;
        return (
          <span className={`px-2 py-1 rounded text-xs font-semibold text-nowrap ${getScheduleTypeClass(simplifiedType)}`}>
            {simplifiedType}
          </span>
        );
      },
    },
    {
      headerName: t('Expression'),
      field: 'simplified_expression',
      flex: 1,
      minWidth: 140,
      cellRenderer: (params: { data?: Schedule }) => params.data?.simplified_expression ? (
        <code className="text-xs bg-default-100 px-2 py-1 mt-2 rounded font-mono text-primary block truncate" title={params.data.simplified_expression}>
          {params.data.simplified_expression}
        </code>
      ) : null,
    },
    {
      headerName: t('State'),
      field: 'state',
      minWidth: 100,
      cellRenderer: (params: { data?: Schedule }) => params.data ? (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${getScheduleStateClass(params.data.state)}`}>
          {params.data.state}
        </span>
      ) : null,
    },
    {
      headerName: t('Target'),
      field: 'target_id',
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => params.data ? `${params.data.target_id}\n${params.data.target_type || ''}` : '',
      cellRenderer: (params: { data?: Schedule }) => params.data ? (
        <div className="min-w-0 overflow-hidden">
          <div className="text-sm truncate" title={params.data.target_id}>{params.data.target_id}</div>
          <div className="text-xs text-default-500 truncate" title={params.data.target_type}>{params.data.target_type}</div>
        </div>
      ) : null,
    },
    {
      headerName: t('Target Type'),
      field: 'target_type',
      minWidth: 110,
      cellRenderer: (params: { data?: Schedule }) => {
        return (
        <span className="font-semibold truncate">
            {params.data?.target_type ?? ''}
          </span>
        );
      },
    },
    {
      headerName: t('Created'),
      field: 'created_at',
      minWidth: 110,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : '-',
    },
    {
      headerName: t('Actions'),
      field: 'actions' as any,
      cellRenderer: ScheduleActionsCell,
      cellRendererParams: {
        onView: handleView,
        onEdit: handleEdit,
        onDelete: handleDelete,
        isDisabled: !hasSchedulerAccess,
      },
      minWidth: 150,
      maxWidth: 150,
      resizable: false,
      pinned: 'right',
      lockPinned: true,
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
    },
  ], [t, handleView, handleEdit, handleDelete]);

  const schedulerColumnDefsWithSort = useMemo(() => mergeColumnDefs(schedulerColumnDefs), [schedulerColumnDefs, mergeColumnDefs]);

  // Schedule details are fetched via useGetScheduleDetailsQuery and used directly via scheduleDetails.

  const renderSchedulerTab = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <CtySpinner size="md" />
          <span className="ml-2 text-default-500">{t('Loading schedules...')}</span>
        </div>
      );
    }

    return (
      <div className="bg-content1 rounded-lg border overflow-hidden">
          {/* Filters and New Schedule Button */}
          <div className="p-4 border-b grid grid-cols-1 md:grid-cols-[minmax(0,200px)_1fr_1fr_auto] gap-3 items-center">
            <Input
              placeholder={t('Search schedules...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startContent={<Icon icon="mdi:magnify" />}
              size="sm"
              className="max-w-[300px]"
            />
            <Select
              placeholder={t('All States')}
              selectedKeys={stateFilter ? [stateFilter] : []}
              onSelectionChange={(keys) => setStateFilter(Array.from(keys)[0] as string || '')}
              size="sm"
              classNames={{ trigger: 'min-w-[150px]' }}
            >
              {STATE_OPTIONS.map((state) => (
                <SelectItem key={state.key} value={state.value}>{t(state.labelKey)}</SelectItem>
              ))}
            </Select>
            <Select
              placeholder={t('All Types')}
              selectedKeys={typeFilter ? [typeFilter] : []}
              onSelectionChange={(keys) => setTypeFilter(Array.from(keys)[0] as string || '')}
              size="sm"
              classNames={{ trigger: 'min-w-[150px]' }}
            >
              <SelectItem key="RATE" value="RATE">{t('Rate')}</SelectItem>
              <SelectItem key="CRON" value="CRON">{t('Cron')}</SelectItem>
              <SelectItem key="AT" value="AT">{t('At (One-time)')}</SelectItem>
            </Select>
            <CtyButton
              size="md"
              color="primary"
              variant="bordered"
              startContent={<Icon icon="mdi:plus" />}
              onPress={() => {
                setPanelMode('add');
                createForm.reset(defaultFormValues);
              }}
              isDisabled={!hasSchedulerAccess}
            >
              {t('Create Schedule')}
            </CtyButton>
          </div>

          {/* Table - AG Grid with client-side pagination (count = items length) */}
          <div className="min-w-0 w-full">
            <div
              className="ag-theme-alpine w-full"
              style={{
                width: '100%',
                '--ag-border-color': '#e0e0e0',
              } as React.CSSProperties}
            >
              <AgGridReact<Schedule>
                rowData={filteredSchedules}
                columnDefs={schedulerColumnDefsWithSort}
                getRowId={(params) => params.data?.scheduler_id }
                onRowClicked={(e) => {
                  const target = e.event?.target as HTMLElement | null;
                  if (target?.closest('button')) return;
                  if (e.data) setActivityLogSchedulerId(e.data.scheduler_id);
                }}
                defaultColDef={{ resizable: true, sortable: true }}
                headerHeight={40}
                rowHeight={48}
                domLayout="autoHeight"
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 40, 50]}
                paginationAutoPageSize={false}
                alwaysShowHorizontalScroll={false}
                onGridReady={onSchedulerGridReady}
              />
            </div>
          </div>
        </div>
    );
  };

  return (
    <div className="flex flex-col gap-2 h-full min-h-0">
      <PageHeaderWithErrorBoundary
        heading={t('Scheduler')}
        description={t('Create and manage scheduled AI agent executions')}
        pageHeaderClass="!px-0 !gap-1 !pb-0"
      />

      <CustomTabs
        tabs={[
          { key: 'scheduler', title: t('Scheduler'), id: 'scheduler-tab' },
          { key: 'reprocess', title: t('Reprocess Job'), id: 'reprocess-tab' },
          { key: 'audit-logs', title: t('Audit Logs'), id: 'audit-logs-tab' },
        ]}
        selectedKey={selectedTab}
        onTabChange={setSelectedTab}
      />
      <div className="pt-4">
        {selectedTab === 'scheduler' && renderSchedulerTab()}
        {selectedTab === 'reprocess' && <ReprocessSettings />}
        {selectedTab === 'audit-logs' && (
          <AuditLogsAgGridWrapper staticFilters={SCHEDULER_AUDIT_LOG_STATIC_FILTERS} />
        )}
      </div>

      {/* Sliding Right Panel - Add/Edit Form */}
        {/* Backdrop */}
        {panelMode && (
          <button
            className="fixed inset-0 bg-black bg-opacity-50 z-[42]"
            onClick={() => {
              setPanelMode(null);
              createForm.reset(defaultFormValues);
              editForm.reset(defaultFormValues);
              setSelectedScheduleId(null);
              resetCustomParamState();
            }}
          />
        )}

        {/* Right Side Panel */}
        <div
          className={`fixed top-0 right-0 h-full w-[500px] bg-content1 shadow-xl z-50 transform transition-transform duration-300 ease-out ${
            panelMode ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full overflow-y-auto">
              <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-content1 z-[1000]">
                  <h2 className="text-lg font-semibold">
                    {panelMode === 'add' ? t('New Schedule') : t('Edit Schedule')}
                  </h2>
                  {/* Close Button */}
                  <CtyButton
                    onClick={() => {
                      setPanelMode(null);
                      createForm.reset(defaultFormValues);
                      editForm.reset(defaultFormValues);
                      setSelectedScheduleId(null);
                      resetCustomParamState();
                    }}
                    className="absolute top-4 right-4"
                    size="sm"
                    variant="light"
                    isIconOnly
                    aria-label={t('Close')}
                  >
                    <Icon icon="mdi:close" className="w-5 h-5 text-default-500" />
                  </CtyButton>
                </div>
                {panelMode === 'add' && (           
                <form onSubmit={createForm.handleSubmit(onSubmit)} className="p-5 space-y-4">
            <Card className="p-6">
              <h3 className="font-medium text-xl border-b pb-2 mb-4">{t('Schedule Metadata')}</h3>
              <div className="space-y-4">
                <Controller
                  name="schedule_name"
                  control={createForm.control}
                  rules={{
                    required: t('Schedule name is required'),
                    pattern: {
                      value: /^[0-9a-zA-Z\-_.]+$/,
                      message: t('Schedule name can only contain letters, numbers, hyphens, underscores, and periods. No spaces allowed.')
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <div>
                      <Input
                        {...field}
                        id="scheduleName"
                        label={t('Schedule Name')}
                        placeholder={t('e.g., daily-report-sync')}
                        isInvalid={!!fieldState.error}
                        errorMessage={fieldState.error?.message}
                        size="sm"
                      />
                      <p className="text-xs text-default-500 mt-1">
                        {t('Allowed characters: letters, numbers, hyphens (-), underscores (_), and periods (.). No spaces allowed.')}
                      </p>
                    </div>
                  )}
                />
                <DescriptionField control={createForm.control} />
              </div>
            </Card>
            <TargetConfigurationsSection
              control={createForm.control}
              handleSelectChange={handleSelectChange}
              customParamsFields={createCustomParamsFields}
              getValues={createForm.getValues}
              customParamMode={customParamMode}
              onCustomParamModeChange={setCustomParamMode}
              customParamJson={customParamJson}
              onCustomParamJsonChange={handleCustomParamJsonChange}
              customParamJsonError={customParamJsonError}
            />
                              
            <ScheduleConfigurationSection
              control={createForm.control}
              setValue={createForm.setValue}
              getValues={createForm.getValues}
              handleSelectChange={handleSelectChange}
              watchedScheduleType={watchedScheduleType}
              use12HourFormat={true}
            />

                  <div className="flex gap-3">
                    <CtyButton
                      variant="light"
                      onPress={() => {
                        setPanelMode(null);
                        createForm.reset(defaultFormValues);
                        resetCustomParamState();
                      }}
                      className="flex-1"
                    >
                      {t('Cancel')}
                    </CtyButton>
                    <CtyButton
                      onPress={() => createForm.handleSubmit(onSubmit)()}
                      color="primary"
                      className="flex-1"
                      isLoading={isCreating}
                      isDisabled={!hasSchedulerAccess || (customParamMode === 'object' && !!customParamJsonError)}
                    >
                      {t('Create Schedule')}
                    </CtyButton>
                  </div>
                </form>
                )}
                {panelMode === 'edit' && (
                <>                 
                {isLoadingDetails || isFetchingDetails ? (
                  <div className="flex items-center justify-center py-8">
                    <CtySpinner size="md" />
                    <span className="ml-2 text-default-500">{t('Loading schedule details...')}</span>
                  </div>
                ) : (
                  <form onSubmit={editForm.handleSubmit(handleUpdate)} className="p-5 space-y-4">
                <Card className="p-6">
                   <h3 className="font-medium text-xl border-b pb-2 mb-4">{t('Schedule Metadata')}</h3>
                      <div className="space-y-4">
                        <Controller
                          name="schedule_name"
                          control={editForm.control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label={t('Schedule Name')}
                              isReadOnly
                              className="opacity-60"
                              size="sm"
                            />
                          )}
                        />
                       <DescriptionField control={editForm.control} />
                      </div>
                    </Card>
                   <TargetConfigurationsSection 
                   control={editForm.control} 
                   handleSelectChange={handleSelectChange} 
                   customParamsFields={editCustomParamsFields} 
                   getValues={editForm.getValues}
                   customParamMode={customParamMode}
                   onCustomParamModeChange={setCustomParamMode}
                   customParamJson={customParamJson}
                   onCustomParamJsonChange={handleCustomParamJsonChange}
                   customParamJsonError={customParamJsonError}
                   />
                    <ScheduleConfigurationSection
                      control={editForm.control}
                      setValue={editForm.setValue}
                      getValues={editForm.getValues}
                      handleSelectChange={handleSelectChange}
                      watchedScheduleType={watchedEditScheduleType}
                      use12HourFormat={false}
                    />
                    <div className="flex gap-3">
                      <CtyButton
                        variant="light"
                        onPress={() => {
                          setPanelMode(null);
                          setSelectedScheduleId(null);
                          editForm.reset(defaultFormValues);
                          resetCustomParamState();
                        }}
                        className="flex-1"
                      >
                        {t('Cancel')}
                      </CtyButton>
                      {panelMode === 'edit' && selectedScheduleId && (
                        <CtyButton
                          variant="bordered"
                          color="primary"
                          onPress={handleRunNow}
                          className="flex-1"
                          isLoading={isRunningSchedule}
                          isDisabled={!hasSchedulerAccess || isUpdating}
                        >
                          {t('Run Now')}
                        </CtyButton>
                      )}
                      <CtyButton
                        color="primary"
                        onPress={() => editForm.handleSubmit(handleUpdate)()}
                        className="flex-1"
                        isLoading={isUpdating}
                        isDisabled={!hasSchedulerAccess || isRunningSchedule || (customParamMode === 'object' && !!customParamJsonError)}
                      >
                        {t('Save Changes')}
                      </CtyButton>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>

      {/* View Modal */}
      <ScheduleDetailsViewModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedScheduleId(null);
        }}
        scheduleId={selectedScheduleId}
      />

      {/* Activity Log Modal - opens on row click */}
      <ActivityLogModal
        isOpen={activityLogSchedulerId != null}
        onClose={() => setActivityLogSchedulerId(null)}
        schedulerId={activityLogSchedulerId}
      />

      <SimpleDeleteConfirmationModal
        isOpen={deleteModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteModalOpen(false);
            setDeleteTargetScheduleName('');
          }
        }}
        entityName={deleteTargetScheduleName}
        secondaryMessage={t('The schedule will be removed from both EventBridge and the database.')}
        onConfirm={confirmDelete}
        isConfirmLoading={isDeleting}
        cancelLabel={t('Close')}
        confirmLabel={t('Delete Schedule')}
      />
    </div>
  );
}

export default SchedulerSettings;
